/**
 * Cross-tab loan navigation for the LMS staff-ops hub (Phase 10B).
 *
 * WHY THIS EXISTS
 * ---------------
 * Tabs 4, 5 and 6 (Overview / Overdue split / Calendar) all render lists of
 * loan IDs which the plan requires to be "hyperlinked to the loan detail /
 * edit view" (plan §5.6 Tasks 6/7/8). There is no dedicated per-loan route in
 * this SPA — the whole feature lives inside a single `AdminDashboard`
 * `activeView`, and the backend deliberately returns raw `intellend_loan_id`
 * values with no server-built URLs (see the docstrings on
 * OverviewMissingPaymentsView / CalendarDayView). So the "hyperlink" is
 * implemented as an in-hub jump: switch the hub's active tab and hand the
 * target tab a pre-selected loan.
 *
 * MECHANISM (deliberately the smallest thing that works)
 * ------------------------------------------------------
 * `LmsStaffOpsHub` owns two pieces of state — the controlled `Tabs` value and
 * a `{tab, loanId, nonce}` "pending loan" record — and publishes a single
 * `openLoan(loanId, tab)` callback plus a `canOpen(tab)` predicate (derived
 * from the same `my_permissions` response that decides tab visibility) through
 * this React context. Consumers never touch tab state directly; they just
 * render `<LoanIdLink loanId={...} />`.
 *
 * The `nonce` is what makes repeat navigations work: Radix `TabsContent`
 * unmounts inactive tabs, so the hub keys each tab body on the nonce, and
 * clicking a second loan ID for the tab you are already on remounts that tab
 * body with the new `initialLoanId` instead of silently doing nothing.
 *
 * No URL params, no global store, no router changes — the context is created
 * and consumed entirely within `pagesadmin/lms_staff/`.
 */
import React from "react";
import { Pencil } from "lucide-react";

import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

import { LmsTabKey } from "./lmsStaffApi";

/** Tabs that accept an `initialLoanId` and are therefore valid jump targets. */
export type LoanNavTarget = Extract<
    LmsTabKey,
    "loan_profile" | "mandate_pg" | "presentation_collection"
>;

export interface LmsStaffNavValue {
    /** Switches the hub to `tab` with `loanId` pre-selected. */
    openLoan: (loanId: string, tab: LoanNavTarget) => void;
    /** False when the current user has no `can_view` on that tab. */
    canOpen: (tab: LoanNavTarget) => boolean;
}

/**
 * Default value is an inert no-op pair rather than `undefined` so any of these
 * tabs can also be rendered standalone (e.g. in isolation during development)
 * without crashing — links simply degrade to plain text.
 */
const LmsStaffNavContext = React.createContext<LmsStaffNavValue>({
    openLoan: () => undefined,
    canOpen: () => false,
});

export const LmsStaffNavProvider: React.FC<{
    value: LmsStaffNavValue;
    children: React.ReactNode;
}> = ({ value, children }) => (
    <LmsStaffNavContext.Provider value={value}>{children}</LmsStaffNavContext.Provider>
);

// This module intentionally pairs the context/provider with its consumer
// hook, matching the existing convention elsewhere in this codebase (e.g.
// contexts/AuthContext.tsx); splitting the hook into its own file would only
// fragment one small, single-purpose module for a fast-refresh nicety.
// eslint-disable-next-line react-refresh/only-export-components
export const useLmsStaffNav = (): LmsStaffNavValue => React.useContext(LmsStaffNavContext);

const LINK_CLASS =
    "font-mono text-xs text-intellend-green underline decoration-dotted hover:decoration-solid";

/**
 * A loan ID rendered as the plan's required hyperlink.
 *
 * The label itself opens Tab 1 (Loan Profile) — the read-only "detail view" —
 * and a small pencil icon-button beside it opens Tab 2 (Mandate & Payments),
 * the "edit view". Whichever of the two the user lacks `can_view` for is
 * omitted; if neither is viewable the ID renders as plain text so the row
 * still reads correctly.
 */
export const LoanIdLink: React.FC<{ loanId: string; className?: string }> = ({
    loanId,
    className,
}) => {
    const { openLoan, canOpen } = useLmsStaffNav();
    const canProfile = canOpen("loan_profile");
    const canMandate = canOpen("mandate_pg");

    if (!canProfile && !canMandate) {
        return <span className={className ?? "font-mono text-xs"}>{loanId}</span>;
    }

    return (
        <TooltipProvider>
            <span className="inline-flex items-center gap-1.5">
                {canProfile ? (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                className={className ?? LINK_CLASS}
                                onClick={() => openLoan(loanId, "loan_profile")}
                            >
                                {loanId}
                            </button>
                        </TooltipTrigger>
                        <TooltipContent>Open in Loan Profile</TooltipContent>
                    </Tooltip>
                ) : (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                className={className ?? LINK_CLASS}
                                onClick={() => openLoan(loanId, "mandate_pg")}
                            >
                                {loanId}
                            </button>
                        </TooltipTrigger>
                        <TooltipContent>Open in Mandate &amp; Payments</TooltipContent>
                    </Tooltip>
                )}

                {canProfile && canMandate && (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                aria-label={`Open ${loanId} in Mandate & Payments`}
                                className="text-gray-400 hover:text-intellend-green"
                                onClick={() => openLoan(loanId, "mandate_pg")}
                            >
                                <Pencil className="h-3.5 w-3.5" />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent>Open in Mandate &amp; Payments</TooltipContent>
                    </Tooltip>
                )}

            </span>
        </TooltipProvider>
    );
};

export default LmsStaffNavContext;
